import { getSearchIndex, searchIndex } from './searchIndex';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { usePages } from '#context';
import { Footprints } from 'lucide-react';

const DEBOUNCE_MS = 400;
const SUGGESTION_LIMIT = 5;

const NavbarSearch = ({ navbarVisible = true }) => {
    const navigate = useNavigate();
    const { mainPages } = usePages();
    const inputRef = useRef(null);
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [activeSuggestion, setActiveSuggestion] = useState(-1);
    const [isFocused, setIsFocused] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (navbarVisible) return;

        inputRef.current?.blur();
    }, [navbarVisible]);

    useEffect(() => {
        const trimmedQuery = query.trim();
        if (!trimmedQuery) return undefined;

        let cancelled = false;
        const timeoutId = window.setTimeout(async () => {
            setLoading(true);
            setError('');
            try {
                const index = await getSearchIndex();
                if (cancelled) return;
                setSuggestions(searchIndex(index, trimmedQuery).slice(0, SUGGESTION_LIMIT));
                setActiveSuggestion(-1);
            } catch (searchError) {
                if (!cancelled) {
                    setSuggestions([]);
                    setError(
                        searchError.message || 'Nie udało się przeszukać podstron i artykułów.',
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [query]);

    const submitSearch = (event) => {
        event.preventDefault();
        const trimmedQuery = query.trim();
        if (!trimmedQuery) return;
        setIsFocused(false);
        navigate(`${mainPages?.search || '/szukaj'}/${encodeURIComponent(trimmedQuery)}`);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'ArrowDown' && suggestions.length > 0) {
            event.preventDefault();
            setActiveSuggestion((current) => (current + 1) % suggestions.length);
        } else if (event.key === 'ArrowUp' && suggestions.length > 0) {
            event.preventDefault();
            setActiveSuggestion((current) =>
                current <= 0 ? suggestions.length - 1 : current - 1,
            );
        }
    };

    const navigateToSuggestion = (href) => {
        setIsFocused(false);
        navigate(href);
    };

    return (
        <form className="anikino__navbar-search-form" onSubmit={submitSearch}>
            <input
                ref={inputRef}
                type="search"
                name="anikino__navbar-search"
                className="anikino__navbar-search"
                placeholder="Szukaj..."
                value={query}
                autoComplete="off"
                onChange={(event) => {
                    const nextQuery = event.target.value;
                    setQuery(nextQuery);
                    setSuggestions([]);
                    setActiveSuggestion(-1);
                    setError('');
                    setLoading(Boolean(nextQuery.trim()));
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onKeyDown={handleKeyDown}
                role="combobox"
                aria-label="Szukaj na stronie"
                aria-autocomplete="list"
                aria-expanded={isFocused && suggestions.length > 0}
                aria-controls="navbar-search-suggestions"
                aria-activedescendant={
                    activeSuggestion >= 0
                        ? `navbar-search-suggestion-${activeSuggestion}`
                        : undefined
                }
            />
            <button
                type="submit"
                className="anikino__navbar-search-button"
                aria-label="Szukaj"
                disabled={!query.trim()}
            >
                <Footprints size={26} aria-hidden="true" />
            </button>
            {isFocused && (loading || error || suggestions.length > 0) && (
                <div className="anikino__navbar-search-suggestions">
                    {loading && (
                        <p className="anikino__navbar-search-status" role="status">
                            Wyszukiwanie…
                        </p>
                    )}
                    {error && (
                        <p className="anikino__navbar-search-status error" role="alert">
                            {error}
                        </p>
                    )}
                    {!loading && suggestions.length > 0 && (
                        <ul id="navbar-search-suggestions" role="listbox">
                            {suggestions.map((suggestion, index) => (
                                <li key={`${suggestion.href}-${suggestion.title}`}>
                                    <button
                                        id={`navbar-search-suggestion-${index}`}
                                        type="button"
                                        role="option"
                                        aria-selected={index === activeSuggestion}
                                        className={index === activeSuggestion ? 'active' : ''}
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => navigateToSuggestion(suggestion.href)}
                                    >
                                        <span>{suggestion.title}</span>
                                        <small>{suggestion.location}</small>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </form>
    );
};

export default NavbarSearch;
